import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, LogOut, Lock, CalendarDays, CheckCircle2, Clock, CalendarX2, Users, History, BellRing } from "lucide-react";
import { cn } from "@/lib/utils";
import { API, TEACHER_TOKEN_KEY, toISODate, shiftDate, formatLongDate, formatShortDate, lessonTitle, lessonRecord } from "@/lib/attendance";
import AttendanceSheet from "@/components/attendance/AttendanceSheet";

export default function TeacherPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TEACHER_TOKEN_KEY) || "");
  const logout = () => {
    localStorage.removeItem(TEACHER_TOKEN_KEY);
    setToken("");
  };
  if (!token) {
    return <TeacherLogin onLogin={(t) => { localStorage.setItem(TEACHER_TOKEN_KEY, t); setToken(t); }} />;
  }
  return <TeacherHome token={token} onLogout={logout} />;
}

function TeacherLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/teacher/login`, { username, password });
      onLogin(res.data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali errate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-4">
      <Card className="w-full max-w-md card-warm">
        <CardHeader>
          <div className="w-16 h-16 rounded-2xl bg-white shadow-md ring-1 ring-slate-200 overflow-hidden mb-3">
            <img src="/logo-ctm.png" alt="Circolo Tennis Montecchio" className="w-full h-full object-contain p-1" />
          </div>
          <CardTitle className="text-2xl">Presenze maestri</CardTitle>
          <CardDescription>Accedi con il tuo nome e la tua password.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="t-user">Nome utente</Label>
              <Input id="t-user" autoCapitalize="none" autoComplete="username" value={username}
                onChange={(e) => setUsername(e.target.value)} className="h-12 text-base" data-testid="teacher-username-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-pass">Password</Label>
              <Input id="t-pass" type="password" autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} className="h-12 text-base" data-testid="teacher-password-input" />
            </div>
            <Button type="submit" disabled={loading || !username || !password} data-testid="teacher-login-btn"
              className="w-full h-12 text-base bg-[#15803D] hover:bg-[#166534] gap-2">
              <Lock className="w-4 h-4" />
              {loading ? "Accesso..." : "Entra"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function TeacherHome({ token, onLogout }) {
  const [date, setDate] = useState(() => toISODate(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLesson, setOpenLesson] = useState(null);
  const [username, setUsername] = useState("");
  const [recoveries, setRecoveries] = useState([]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const handleErr = (err) => {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      toast.error("Sessione scaduta, accedi di nuovo");
      onLogout();
    } else {
      toast.error(err?.response?.data?.detail || "Errore di caricamento");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, r] = await Promise.all([
        axios.get(`${API}/teacher/lessons`, { ...auth, params: { date } }),
        axios.get(`${API}/teacher/recoveries`, auth),
      ]);
      setData(l.data);
      setRecoveries(r.data);
    } catch (err) {
      handleErr(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [date, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/teacher/me`, auth).then((r) => setUsername(r.data.username)).catch(handleErr);
    // eslint-disable-next-line
  }, [token]);

  const isToday = data && date === data.today;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/90 border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-ctm.png" alt="" className="w-10 h-10 rounded-xl bg-white ring-1 ring-slate-200 object-contain p-0.5" />
            <div>
              <div className="font-bold text-slate-900 leading-tight">Presenze</div>
              <div className="text-xs text-slate-500 capitalize" data-testid="teacher-name">{username}</div>
            </div>
          </div>
          <Button variant="outline" onClick={onLogout} className="h-11 gap-1.5" data-testid="teacher-logout-btn">
            <LogOut className="w-4 h-4" /> Esci
          </Button>
        </div>
        <div className="max-w-2xl mx-auto px-4 pb-3 flex items-center gap-2">
          <Button variant="outline" onClick={() => setDate(shiftDate(date, -1))} className="h-12 w-12 p-0 shrink-0" data-testid="date-prev-btn" aria-label="Giorno precedente">
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <div className="flex-1 text-center">
            <div className="text-base font-bold text-slate-900 capitalize" data-testid="current-date-label">{formatLongDate(date)}</div>
            {isToday ? (
              <div className="text-xs font-semibold text-[#15803D] uppercase tracking-wider">Oggi</div>
            ) : (
              <button onClick={() => data && setDate(data.today)} className="text-xs font-semibold text-[#C85A32] underline" data-testid="date-today-btn">
                Torna a oggi
              </button>
            )}
          </div>
          <Button variant="outline" onClick={() => setDate(shiftDate(date, 1))} className="h-12 w-12 p-0 shrink-0" data-testid="date-next-btn" aria-label="Giorno successivo">
            <ChevronRight className="w-6 h-6" />
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        {data && !data.editable && (
          <div className="rounded-xl border border-slate-300 bg-slate-100 text-slate-700 px-4 py-3 text-sm flex gap-2" data-testid="locked-banner">
            <Lock className="w-4 h-4 mt-0.5 shrink-0" /> Registri chiusi: è passata più di una settimana, puoi solo consultarli.
          </div>
        )}
        <LessonList loading={loading} data={data} onOpen={setOpenLesson} />
        <RecoveryList items={recoveries} />
      </main>

      {openLesson && (
        <AttendanceSheet
          token={token}
          date={date}
          lesson={openLesson}
          editable={data.editable}
          isFuture={data.today < date}
          onClose={() => setOpenLesson(null)}
          onSaved={() => { setOpenLesson(null); load(); }}
          onAuthError={handleErr}
        />
      )}
    </div>
  );
}

function LessonList({ loading, data, onOpen }) {
  if (loading && !data) {
    return <div className="py-16 text-center text-slate-500" data-testid="lessons-loading">Caricamento...</div>;
  }
  if (!data?.version_name) {
    return (
      <EmptyState icon={CalendarDays} testid="no-version-state" title="Calendario non ancora pubblicato"
        text="L'admin non ha ancora salvato una versione del calendario. Riprova più tardi." />
    );
  }
  if (!data.lessons.length) {
    return <EmptyState icon={CalendarX2} testid="no-lessons-state" title="Nessuna lezione" text="Non ci sono lezioni in calendario per questo giorno." />;
  }
  return (
    <div className="space-y-3" data-testid="lessons-list">
      {data.lessons.map((l) => <LessonCard key={l.lesson_key} lesson={l} onOpen={() => onOpen(l)} />)}
      <div className="text-[11px] text-slate-400 text-center pt-1">Calendario: {data.version_name}</div>
    </div>
  );
}

function LessonCard({ lesson, onOpen }) {
  const rec = lessonRecord(lesson);
  const present = rec ? lesson.parts.reduce((n, p) => n + p.record.present_ids.length + p.record.guests.filter((g) => g.present).length, 0) : 0;
  const total = lesson.parts.reduce((n, p) => n + p.students.length - (p.optional_ids || []).length, 0);
  return (
    <button onClick={onOpen} data-testid={`lesson-card-${lesson.parts[0].group_id}-${lesson.start_time}`}
      className="w-full text-left bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex active:scale-[0.99] transition-transform">
      <div className="w-2 shrink-0 flex flex-col">
        {lesson.parts.map((p) => <div key={p.group_id} className="flex-1" style={{ backgroundColor: p.color }} />)}
      </div>
      <div className="flex-1 p-4 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-mono text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <Clock className="w-4 h-4" /> {lesson.start_time}–{lesson.end_time}
          </div>
          <LessonStatus rec={rec} present={present} />
        </div>
        <div className="mt-1 text-lg font-bold text-slate-900">
          {lessonTitle(lesson)}
        </div>
        <div className="text-sm text-slate-500 flex items-center gap-3 flex-wrap">
          <span>{lesson.court_nome}</span>
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {total}</span>
          {lesson.coaches.length > 0 && <span>{lesson.coaches.join(", ")}</span>}
          {lesson.parts.some((p) => p.reminder) && (
            <span className="flex items-center gap-1 text-amber-700 font-semibold" data-testid="lesson-reminder-flag">
              <BellRing className="w-3.5 h-3.5" /> Promemoria
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function LessonStatus({ rec, present }) {
  if (!rec) return <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-slate-100 text-slate-600">Da registrare</span>;
  if (rec.status === "postponed") {
    return <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-amber-100 text-amber-800" data-testid="status-postponed">Rimandata</span>;
  }
  return (
    <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-green-100 text-green-800 flex items-center gap-1" data-testid="status-registered">
      <CheckCircle2 className="w-3.5 h-3.5" /> {present} presenti
    </span>
  );
}

function RecoveryList({ items }) {
  if (!items.length) return null;
  return (
    <section className="pt-4" data-testid="teacher-recoveries">
      <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
        <History className="w-4 h-4" /> Lezioni da recuperare ({items.length})
      </h2>
      <div className="space-y-2">
        {items.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-amber-200 px-4 py-3 text-sm">
            <div className="font-semibold text-slate-900">{r.group_nome} · {formatShortDate(r.date)} {r.start_time}</div>
            <div className="text-slate-600">{r.postpone_reason}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function EmptyState({ icon: Icon, title, text, testid }) {
  return (
    <div className={cn("py-14 px-6 text-center bg-white rounded-2xl border border-dashed border-slate-300")} data-testid={testid}>
      <Icon className="w-10 h-10 mx-auto text-slate-300 mb-3" />
      <div className="font-bold text-slate-800">{title}</div>
      <div className="text-sm text-slate-500 mt-1">{text}</div>
    </div>
  );
}
